import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spotlight, cx, spotlightRect } from '@/components/ui';
import { useTourGuide } from './TourContext';
import { computeTooltipPlacement, getTargetRect, queryTourTarget, scrollTargetIntoView, type RectLike } from './tourUtils';
import { KIOSK_TOUR_ACTIVATE_EVENT } from './types';

/** Space between the highlighted element and the edge of the spotlight. */
const SPOTLIGHT_PADDING = 8;

function formatStepCounter(stepIndex: number, total: number): string {
  const x = Math.min(stepIndex + 1, Math.max(total, 1));
  return `${x} / ${Math.max(total, 1)}`;
}

export function TourOverlay(props: {
  onRequestClose: () => void;
  onRequestFinish: () => void;
}) {
  const { onRequestClose, onRequestFinish } = props;
  const { t } = useTranslation();
  const { status, steps, stepIndex, next, back, finish } = useTourGuide();

  const step = steps[stepIndex];
  const isRunning = status === 'running';
  const total = steps.length;

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<RectLike | null>(null);
  const missingTargetRetriesRef = useRef(0);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  /** The spotlight's cut-out; the tooltip is placed next to it. */
  const paddedRect = useMemo(
    () => (targetRect ? (spotlightRect(targetRect, SPOTLIGHT_PADDING) satisfies RectLike) : null),
    [targetRect]
  );

  // Resolve target element on step change
  useEffect(() => {
    if (!isRunning || !step) return;
    missingTargetRetriesRef.current = 0;
    let cancelled = false;
    let retryTimer = 0;

    // Let multi-step pages mount the matching panel before we query the DOM.
    if (step.activate) {
      window.dispatchEvent(
        new CustomEvent(KIOSK_TOUR_ACTIVATE_EVENT, { detail: step.activate })
      );
    }

    const tryResolve = () => {
      if (cancelled) return;
      const el = queryTourTarget(step.targetTourId);
      setTargetEl(el);
      if (el) {
        scrollTargetIntoView(el);
        return;
      }

      // Retry a few times to handle async rendering/data load.
      missingTargetRetriesRef.current += 1;
      if (missingTargetRetriesRef.current <= 16) {
        retryTimer = window.setTimeout(tryResolve, 250);
        return;
      }

      // Skip this step if target is still missing.
      if (stepIndex < steps.length - 1) next();
      else {
        finish();
        onRequestFinish();
      }
    };

    // Small delay so React can commit wizard step changes from activate.
    retryTimer = window.setTimeout(tryResolve, step.activate ? 80 : 0);
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [isRunning, step, stepIndex, steps.length, next, finish, onRequestFinish]);

  // Track rect on scroll/resize while running
  useEffect(() => {
    if (!isRunning || !targetEl) return;
    let raf = 0;

    const update = () => {
      raf = window.requestAnimationFrame(() => {
        setTargetRect(getTargetRect(targetEl));
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isRunning, targetEl]);

  // Compute tooltip position after tooltip renders / rect updates
  useLayoutEffect(() => {
    if (!isRunning || !step || !paddedRect || !tooltipRef.current) {
      setTooltipPos(null);
      return;
    }
    const box = tooltipRef.current.getBoundingClientRect();
    const res = computeTooltipPlacement({
      targetRect: paddedRect,
      tooltipSize: { width: box.width, height: box.height },
      preferred: step.placement ?? 'auto',
      viewportPadding: 12,
      gap: 12,
    });
    setTooltipPos({ top: res.top, left: res.left });
  }, [isRunning, step, paddedRect]);

  useEffect(() => {
    if (!isRunning) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isRunning, onRequestClose]);

  if (!isRunning || !step) return null;

  const canBack = stepIndex > 0;
  const isLast = stepIndex === total - 1;

  const handleNext = () => {
    if (isLast) {
      finish();
      onRequestFinish();
    } else {
      next();
    }
  };

  const handleClose = () => {
    onRequestClose();
  };

  return (
    // Spotlight: full-screen dim with the cut-out, on the popover layer; it swallows every
    // click, wheel and touch on the page. The tooltip is the only interactive surface.
    <Spotlight target={targetRect} padding={SPOTLIGHT_PADDING}>
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="pointer-events-auto fixed flex flex-col gap-3 rounded-container border border-edge bg-raised p-4 shadow-raised"
        style={{
          width: 'min(22.5rem, calc(100vw - 1.5rem))',
          top: tooltipPos ? `${tooltipPos.top}px` : '50%',
          left: tooltipPos ? `${tooltipPos.left}px` : '50%',
          transform: tooltipPos ? 'translate3d(0,0,0)' : 'translate(-50%,-50%)',
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-1.5">
          <h2 id={titleId} className="m-0 font-display text-title font-semibold text-primary">
            {step.title}
          </h2>
          <p id={descriptionId} className="m-0 font-ui text-small text-secondary">
            {step.description}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" aria-hidden>
          {steps.map((_, i) => (
            <span key={i} className={cx('h-1.5 rounded-full', i === stepIndex ? 'w-4 bg-beam' : 'w-1.5 bg-selected')} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={handleClose}>
            {t('tour.skip')}
          </Button>
          <div className="flex-1 text-center font-ui text-small font-semibold tabular-nums text-tertiary">
            {formatStepCounter(stepIndex, total)}
          </div>
          {canBack ? (
            <Button type="button" variant="ghost" size="sm" onClick={back}>
              {t('common.back')}
            </Button>
          ) : null}
          <Button type="button" variant="primary" size="sm" onClick={handleNext}>
            {isLast ? t('tour.finish') : t('tour.next')}
          </Button>
        </div>
      </div>
    </Spotlight>
  );
}
